import { DataTable, footers, useColumns } from "@/components/data-table";
import { useFilters } from "@/components/filters";
import { ListPage } from "@/components/list-page";
import { useOpenModal } from "@/components/modal";
import { getAllClientes } from "@/features/auxiliar/clientes/api/get-all-clientes";
import { getCliente } from "@/features/auxiliar/clientes/api/get-cliente";
import { getAllVendedores } from "@/features/auxiliar/vendedores/api/get-all-vendedores";
import { getVendedor } from "@/features/auxiliar/vendedores/api/get-vendedor";
import CsvExportDialog from "@/features/export/components/csv-export-dialog";
import PrintComponent from "@/features/export/components/print-component";
import PrintExportDialog from "@/features/export/components/print-export-dialog";
import XlsxExportDialog from "@/features/export/components/xlsx-export-dialog";
import { getAllArticulos } from "@/features/inventario/articulos/api/get-all-articulos";
import { getArticulo } from "@/features/inventario/articulos/api/get-articulo";
import useMonetizacion from "@/features/monetizacion/hooks/use-monetizacion";
import { getVentaItemReporte, ReporteVentaItem, useGetVentaItemReporteQuery } from "@/features/reportes/api/get-venta-item-reporte";
import { formatDate, formatNumber, formatString } from "@/shared/formats";
import { Typography } from "@mui/material";

export default function VentaItemReportes() {
    const openModal = useOpenModal();

    const { monedaBase } = useMonetizacion();

    const filterData = useFilters({
        filters: [
            { type: "dateRange", label: "Fecha", fromName: "desde", toName: "hasta" },
            { type: "model", label: "Artículo", name: "articuloId", get: getArticulo, getAll: getAllArticulos },
            { type: "model", label: "Cliente", name: "clienteId", get: getCliente, getAll: getAllClientes },
            { type: "model", label: "Vendedor", name: "vendedorId", get: getVendedor, getAll: getAllVendedores },
            {
                type: "select", label: "Condición", name: "credito", options: [
                    { label: <i>Todos</i>, value: "" },
                    { label: "Contado", value: false },
                    { label: "Crédito", value: true }
                ]
            },
            { type: "pagination" }
        ]
    });

    const hasFilters = filterData.filters.some(f => f.active);
    const fetchData = useGetVentaItemReporteQuery(filterData.filterData, {
        enabled: hasFilters
    });

    const columnData = useColumns<ReporteVentaItem>({
        columns: [
            { key: "numVenta", header: "N°", render: row => formatString(row.numeroVenta), width: "auto" },
            { key: "fecha", header: "Fecha", render: row => formatDate(row.fecha), width: "auto" },
            { key: "numComprobante", header: "N° de Comprobante", render: row => formatString(row.numComprobante), show: false },
            { key: "articulo", header: "Artículo", render: row => formatString(row.articuloNombre) },
            { key: "cliente", header: "Cliente", render: row => formatString(row.clienteNombre), show: false },
            { key: "vendedor", header: "Vendedor", render: row => formatString(row.vendedorNombre), show: false },
            { key: "condicion", header: "Condición", render: row => row.credito ? "Crédito" : "Contado", width: "auto", align: "center", show: false },
            { key: "costo", header: `Costo (${monedaBase.simbolo})`, render: row => formatNumber(row.costoUnitario), show: false },
            { key: "precio", header: `Precio (${monedaBase.simbolo})`, render: row => formatNumber(row.precioUnitario) },
            { key: "cantidad", header: "Cantidad", render: row => formatNumber(row.cantidad) },
            { key: "total", header: `Total (${monedaBase.simbolo})`, render: row => formatNumber(row.total), align: "right", width: "auto" },
            { key: "utilidad", header: `Utilidad (${monedaBase.simbolo})`, render: row => formatNumber(row.utilidad), align: "right", width: "auto" },
        ],
        key: "reporte_venta_items"
    });

    const footerData = footers([
        { render: () => "Total", span: "fill" },
        { render: () => formatNumber(fetchData.data?.total), from: "total", align: "right" },
        { render: () => formatNumber(fetchData.data?.utilidad), from: "utilidad", align: "right" }
    ]);

    return <ListPage>
        <ListPage.Header>
            <ListPage.Header.Actions title="Detalles de Venta">
                <ListPage.Header.Actions.FilterButton />
                <ListPage.Header.Actions.ColumnButton columnData={columnData} />
                <ListPage.Header.Actions.Divider />
                <ListPage.Header.Actions.ExportButton onClick={() => openModal("print")} disabled={!hasFilters}>
                    <ListPage.Header.Actions.ExportButton.Print onClick={() => openModal("print")} />
                    <ListPage.Header.Actions.ExportButton.Csv onClick={() => openModal("csv")} />
                    <ListPage.Header.Actions.ExportButton.Xlsx onClick={() => openModal("xlsx")} />
                </ListPage.Header.Actions.ExportButton>
            </ListPage.Header.Actions>
            <ListPage.Header.Filters filterData={filterData} />
        </ListPage.Header>
        <DataTable flex={1}>
            <DataTable.Content
                fit="flex"
                rows={fetchData.data?.result}
                columns={columnData.columns}
                footers={footerData}
                loading={fetchData.isFetching}
                error={fetchData.error}
                emptyText={!hasFilters ? "Aplica un filtro para generar un reporte." : undefined}
            />
        </DataTable>

        <PrintExportDialog
            code="print"
            defaultFileTitle="Reporte de Detalles de Venta"
            printComponent={async formValue => {
                const data = await getVentaItemReporte(filterData.filterData);
                return <PrintComponent>
                    <PrintComponent.Header title="Reporte de Detalles de Venta" />
                    <Typography>{formValue.description}</Typography>
                    <PrintComponent.Criterias criterias={await filterData.getCriteria()} />
                    <DataTable>
                        <DataTable.Content
                            columns={columnData.columns}
                            rows={data.result}
                            footers={footerData}
                        />
                    </DataTable>
                </PrintComponent>
            }}
        />

        <CsvExportDialog
            code="csv"
            columns={columnData.columns}
            defaultFileTitle="Reporte de Detalles de Venta"
            fetchFn={getVentaItemReporte}
            filterData={filterData}
        />

        <XlsxExportDialog
            code="xlsx"
            columns={columnData.columns}
            defaultFileTitle="Reporte de Detalles de Venta"
            fetchFn={getVentaItemReporte}
            filterData={filterData}
        />

    </ListPage>
}